import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { EvidenceBadge, ConfidentialBadge } from "@/components/common/EvidenceBadge";

function Block({ title, children }) {
  return (
    <div className="border-t border-[var(--line)] py-6">
      <p className="eyebrow mb-3">{title}</p>
      {children}
    </div>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setP(null);
    setError(false);
    api.get(`/projects/${slug}`).then(({ data }) => setP(data)).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="container-dbrf py-32 text-center">
        <p className="font-display text-2xl text-navy">Project not found.</p>
        <Link to="/projects" className="mt-4 inline-block text-[var(--cobalt)] link-underline">Back to projects</Link>
      </div>
    );
  if (!p) return <div className="container-dbrf py-32 text-center font-mono text-sm text-[var(--mineral)]">Loading…</div>;

  return (
    <>
      <PageMeta title={p.public_title} description={p.short_challenge} />
      <div className="border-b border-[var(--line)] bg-[var(--paper)]">
        <div className="container-dbrf py-12 lg:py-16">
          <Link to="/projects" data-testid="back-to-projects" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--cobalt)]"><ArrowLeft size={15} /> Projects</Link>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <ConfidentialBadge />
            {(p.evidence_statuses || [p.evidence_status]).map((e) => <EvidenceBadge key={e} status={e} />)}
            <span className="rounded-full border border-[var(--line)] px-2.5 py-1 font-mono text-xs text-[var(--mineral)]">{p.project_stage}</span>
          </div>
          <h1 className="max-w-3xl font-display font-extrabold text-navy" style={{ fontSize: "clamp(2rem,4vw,3.4rem)", lineHeight: 1.04 }}>{p.public_title}</h1>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <span className="text-[var(--mineral)]">Client:</span><span className="font-medium text-navy">{p.client_display}</span>
            <span className="text-[var(--mineral)]">· Engagement:</span><span className="font-medium text-navy">{p.engagement_type}</span>
          </div>
          {p.technology_names?.length > 0 && (
            <p className="mt-2 font-mono text-xs text-[var(--mineral)]">Public technology names: {p.technology_names.join(", ")}</p>
          )}
        </div>
      </div>

      <div className="container-dbrf grid gap-12 py-14 lg:grid-cols-[1fr_300px]">
        <div className="reading">
          <Block title="Challenge"><p className="text-[var(--mineral)]">{p.challenge}</p></Block>
          <Block title="Why the problem is difficult"><p className="text-[var(--mineral)]">{p.why_difficult}</p></Block>
          <Block title="DBRF OEM contribution"><p className="text-[var(--mineral)]">{p.dbrf_role}</p></Block>
          {p.public_description && <Block title="Public description"><p className="text-[var(--mineral)]">{p.public_description}</p></Block>}
          <Block title="Approach">
            <ol className="space-y-3">{(p.approach || []).map((a, i) => <li key={a} className="flex gap-3"><span className="font-mono text-sm text-[var(--cobalt)]">{String(i + 1).padStart(2, "0")}</span><span className="text-[var(--mineral)]">{a}</span></li>)}</ol>
          </Block>
          <Block title="Work packages">
            <ul className="space-y-2 text-[var(--mineral)]">{(p.work_packages || []).map((w) => <li key={w} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--amber)]" />{w}</li>)}</ul>
          </Block>
          <Block title="Outputs">
            <div className="flex flex-wrap gap-2">{(p.output_types || []).map((o) => <span key={o} className="rounded-full bg-[var(--cobalt)]/10 px-3 py-1 text-sm text-[var(--cobalt)]">{o}</span>)}</div>
          </Block>
          <Block title="Skills assembled">
            <div className="flex flex-wrap gap-2">{(p.skills_required || []).map((s) => <span key={s} className="rounded-full border border-[var(--line)] bg-[var(--ivory)] px-3 py-1 text-sm text-navy">{s}</span>)}</div>
          </Block>
          <Block title="Evidence and validation boundary"><p className="text-[var(--mineral)]">{p.validation_boundary}</p></Block>
          {p.claims_rule && <div className="rounded-xl border border-[var(--warning)]/30 bg-[var(--warning)]/10 p-4"><p className="font-mono text-sm text-[var(--warning)]">Claims discipline: {p.claims_rule}</p></div>}
          {p.safety_language && <div className="mt-4 rounded-xl border border-[var(--line)] bg-[var(--ivory)] p-4"><p className="text-sm text-[var(--mineral)]">{p.safety_language}</p></div>}
          {p.ip_note && <div className="mt-4 rounded-xl border border-[var(--line)] bg-[var(--ivory)] p-4"><p className="eyebrow mb-1">IP status</p><p className="text-sm text-[var(--mineral)]">{p.ip_note}</p></div>}
          <Block title="IP / rights statement"><p className="text-[var(--mineral)]">{p.rights_statement}</p></Block>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          {p.related_capability_records?.length > 0 && (
            <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <p className="eyebrow mb-3">Related capabilities</p>
              <ul className="space-y-2">
                {p.related_capability_records.map((c) => (
                  <li key={c.slug}><Link to={`/capabilities/${c.slug}`} className="text-sm text-navy hover:text-[var(--cobalt)] link-underline">{c.title}</Link></li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-2xl border border-[var(--line)] bg-[var(--navy)] p-7 text-[var(--ivory)]">
            <p className="font-display text-lg font-bold">Bring a related challenge</p>
            <p className="mt-2 text-sm text-[var(--ivory)]/70">DBRF will assess fit and the capabilities required.</p>
            <Link to="/bring-a-challenge" data-testid="project-cta" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--amber)] px-5 py-3 text-sm font-semibold text-[var(--navy)]">Start a challenge brief <ArrowRight size={15} /></Link>
          </div>
        </aside>
      </div>
    </>
  );
}
